import { parseEntity, picksIn, sectionNamed } from './entity';
import type { Pick } from './entity';
import { rebased } from './rebase';

/**
 * The face, as an edit to the file.
 *
 * A recording's face is not a rendering of picked-ness — it IS how the vault
 * carries it. `# Face` lists the unit root's Favorites, `## Suggestions` lists
 * the candidates the LLM proposed, and the app reads a changed `# Face` back
 * as a Favorites toggle: `VaultImporter.flowBackFace` diffs the file's bullets
 * against the app's own copy of the edges and calls
 * `FavoritesActionsManager.toggleThreadFavoriteIn` for the difference. So
 * picking something here is the same act as picking it on the phone, not a
 * web-only annotation.
 *
 * That merge is three-way, and its BASE is the edges as last exported — which
 * live in `.manifests/<id>.jsonl` now, not in the note. The web never reads
 * them; what it owes the app is a note whose readable half says what the user
 * meant and whose machine tail is untouched, so everything below is shaped by
 * rewriting only the bullets:
 *
 * - **The section ends at the first blank line or next heading**, which is the
 *   terminator `FileVaultSerializer.parseFaceEdit` uses. Bullets stay
 *   contiguous.
 * - **Only bullets go inside it.** One non-bullet line makes the app's parser
 *   return null and abandon the whole flow-back rather than risk reading a
 *   garbled section as an empty one and mass-unpicking.
 * - **An empty face keeps its header.** A present-but-empty section is an
 *   intentional clear; a section that is GONE means "leave it alone". Deleting
 *   the header to unpick the last item would silently do nothing.
 */

const FACE_HEADER = /^#{1,2}[ \t]+Face[ \t]*$/;

/** The picks [raw]'s `# Face` holds. */
export function facePicks(path: string, raw: string): Pick[] {
  return picksIn(sectionNamed(parseEntity(path, raw), 'face')?.body ?? '');
}

/** [after], the page's edit of the face it read as [before], made again on
 *  the face as the file holds it now — see [rebased]. */
export function faceRebased(path: string, current: string, before: readonly Pick[], after: readonly Pick[]): Pick[] {
  return rebased(facePicks(path, current), before, after, (p) => p.ref, (p) => `${p.ref} ${p.text}`);
}

/** [picks] written into [raw]'s `# Face`, with front matter, every other
 *  section and the machine tail left byte for byte. */
export function withFace(raw: string, picks: Pick[]): string {
  const lines = raw.split('\n');
  const start = lines.findIndex((line) => FACE_HEADER.test(line.trim()));
  if (start < 0) return insertFace(raw, picks);

  let end = start + 1;
  while (end < lines.length && !endsSection(lines[end]!)) end++;

  return [...lines.slice(0, start + 1), ...bullets(picks), ...lines.slice(end)].join('\n');
}

/**
 * Every entry the expanded list shows: the candidates in the order the file
 * lists them, then anything on the face that was never a suggestion — a
 * transcript pick made by hand, which is on the face without having been
 * proposed.
 *
 * Suggestion order rather than face order so that toggling an entry changes
 * only its mark and never its position.
 */
export function mergedPicks(face: Pick[], suggestions: Pick[]): Pick[] {
  const proposed = new Set(suggestions.map((p) => p.ref));
  return [...suggestions, ...face.filter((p) => !proposed.has(p.ref))];
}

/** A face with no header yet. Writing a bare header to declare an empty face
 *  would read as a clear of something that was never there. */
function insertFace(raw: string, picks: Pick[]): string {
  if (picks.length === 0) return raw;
  const front = raw.match(/^---\n[\s\S]*?\n---\n*/);
  const head = front ? front[0] : '';
  return `${head}# Face\n${bullets(picks).join('\n')}\n\n${raw.slice(head.length)}`;
}

function bullets(picks: Pick[]): string[] {
  return picks.map((p) => `- [${oneline(p.text)}](${p.ref})`);
}

function endsSection(line: string): boolean {
  const trimmed = line.trim();
  return trimmed === '' || trimmed.startsWith('#');
}

/** The bullet is one line by construction — a newline inside it would end the
 *  section early and drop every pick below it. */
function oneline(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}
